import React, { useState, useEffect } from "react";
import {
	Alert,
	Container,
	Row,
	Col,
	Card,
	Form,
	Button,
	Table,
} from "react-bootstrap";
import Modal from "../common/components/ThemedModal";
import {
	getResults,
	importResultsPdfPreview,
	upsertResults,
} from "../../services/resultService";
import { updateLeaderboard } from "../../services/leaderboardService";
import { addBrandTeamToTeamResults } from "../../services/teamResultService";
import {
	initialEvent,
	Result,
	Event,
	initialSport,
	initialSeries,
	Series,
	Sport,
	RiderWithSports,
	Season,
	initialSeason,
} from "../../types/model";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { RootState } from "../../store/store";
import {
	fetchRidersThunk,
	fetchSeasonsThunk,
	fetchSeriesThunk,
	fetchSportsThunk,
	getEventsThunk,
} from "../../store/thunks/fantasyThunk";
import { FantasySports, ResultPhase } from "../../constants/constants";
import AdminLayout from "./AdminLayout";
import AdminResultsInstructionsAccordion from "./components/AdminResultsInstructionsAccordion";

const getEntityId = (value: unknown): string => {
	if (typeof value === "string") {
		return value;
	}

	if (value && typeof value === "object" && "_id" in value) {
		const id = (value as { _id?: unknown })._id;
		return typeof id === "string" ? id : "";
	}

	return "";
};

const emptyPhaseResult = () => ({
	split1: "",
	split2: "",
	time: "",
	score: 0,
	points: 0,
	rank: 0,
});

type ResultsPdfImportRow = {
	rank: number | null;
	bib: string;
	name: string;
	uciId: string;
	time: string;
	points: number;
	rider?: {
		_id: string;
		first_name: string;
		last_name: string;
		country?: string;
		gender?: string;
	};
};

type ResultsPdfImportSummary = {
	parsedCount: number;
	matchedCount: number;
	unmatchedCount: number;
	matches: ResultsPdfImportRow[];
	unmatchedRows: ResultsPdfImportRow[];
};

type ResultsPdfReviewRow = ResultsPdfImportRow & {
	key: string;
	selectedRiderId: string;
	riderSearch: string;
	isExactMatch: boolean;
	ignored: boolean;
	bestGuessRiderId?: string;
	bestGuessScore?: number;
};

const BEST_GUESS_ASSIGNMENT_THRESHOLD = 0.75;

const getImportErrorMessage = (error: unknown) => {
	if (
		error &&
		typeof error === "object" &&
		"response" in error &&
		error.response &&
		typeof error.response === "object" &&
		"data" in error.response
	) {
		const responseData = error.response.data;
		if (typeof responseData === "string") {
			return responseData;
		}
	}

	return error instanceof Error ? error.message : "Unknown error";
};

const getPhaseLabel = (phase: ResultPhase) => {
	switch (phase) {
		case ResultPhase.Qualifying:
			return "Qualifying";
		case ResultPhase.Semis:
			return "Semis";
		case ResultPhase.Finals:
			return "Finals";
		default:
			return "Selected Phase";
	}
};

const normalizeImportName = (value: string) =>
	value
		.normalize("NFD")
		.replace(/[\u0300-\u036f]/g, "")
		.replace(/[Øø]/g, "O")
		.replace(/[Ææ]/g, "AE")
		.replace(/[Œœ]/g, "OE")
		.replace(/[Ðð]/g, "D")
		.replace(/[Þþ]/g, "TH")
		.replace(/[^A-Z0-9]+/gi, " ")
		.trim()
		.replace(/\s+/g, " ")
		.toUpperCase();

const levenshteinDistance = (first: string, second: string) => {
	const previousRow = Array.from({ length: second.length + 1 }, (_, index) => index);

	for (let i = 0; i < first.length; i++) {
		const currentRow = [i + 1];

		for (let j = 0; j < second.length; j++) {
			const insertCost = currentRow[j] + 1;
			const deleteCost = previousRow[j + 1] + 1;
			const replaceCost = previousRow[j] + (first[i] === second[j] ? 0 : 1);

			currentRow.push(Math.min(insertCost, deleteCost, replaceCost));
		}

		previousRow.splice(0, previousRow.length, ...currentRow);
	}

	return previousRow[second.length];
};

const getNameSimilarity = (first: string, second: string) => {
	const normalizedFirst = normalizeImportName(first);
	const normalizedSecond = normalizeImportName(second);

	if (!normalizedFirst || !normalizedSecond) {
		return 0;
	}

	if (normalizedFirst === normalizedSecond) {
		return 1;
	}

	const compactFirst = normalizedFirst.replace(/\s/g, "");
	const compactSecond = normalizedSecond.replace(/\s/g, "");
	const distance = levenshteinDistance(compactFirst, compactSecond);
	const maxLength = Math.max(compactFirst.length, compactSecond.length);

	return maxLength === 0 ? 0 : Math.max(0, 1 - distance / maxLength);
};

const getRiderDisplayName = (rider: RiderWithSports) =>
	`${rider.last_name}, ${rider.first_name}`;

const AdminResultsPage = () => {
	const { events, sports, series, seasons, riders } = useAppSelector(
		(state: RootState) => state.fantasy,
	);
	const dispatch = useAppDispatch();

	const [resultsData, setResultsData] = useState<Result[]>([]);
	const [selectedSeries, setSelectedSeries] = useState<Series>(initialSeries);
	const [selectedSport, setSelectedSport] = useState<Sport>(initialSport);
	const [selectedSeason, setSelectedSeason] = useState<Season>(initialSeason);
	const [selectedEvent, setSelectedEvent] = useState<Event>(initialEvent);
	const [filteredRiders, setFilteredRiders] = useState<RiderWithSports[]>([]);
	const [selectedPhase, setSelectedPhase] = useState<ResultPhase>(
		ResultPhase.Qualifying,
	);
	const [riderSeachField, setRiderSearchField] = useState("");
	const [riderSearchResults, setRiderSearchResults] =
		useState<RiderWithSports[]>(filteredRiders);
	const [sort, setSort] = useState("asc");
	const [genderFilter, setGenderFilter] = useState("m");
	const [loadedResultsEventId, setLoadedResultsEventId] = useState("");
	const [loadedResultsCount, setLoadedResultsCount] = useState<number | null>(
		null,
	);
	const [isLoadingResults, setIsLoadingResults] = useState(false);
	const [selectedResultsPdf, setSelectedResultsPdf] = useState<File | null>(
		null,
	);
	const [isImportingPdf, setIsImportingPdf] = useState(false);
	const [pdfImportSummary, setPdfImportSummary] =
		useState<ResultsPdfImportSummary | null>(null);
	const [showPdfReviewModal, setShowPdfReviewModal] = useState(false);
	const [pdfReviewRows, setPdfReviewRows] = useState<ResultsPdfReviewRow[]>([]);
	const selectedPhaseLabel = getPhaseLabel(selectedPhase);

	const filteredEvents = events.filter(
		(event: Event) =>
			event.series._id === selectedSeries._id &&
			event.sport._id === selectedSport._id &&
			event.season === selectedSeason._id,
	);

	useEffect(() => {
		fetchEvents();
		fetchSeries();
		fetchSports();
		fetchRiders();
		fetchSeasons();
	}, []);

	useEffect(() => {
		setRiderSearchResults(
			filteredRiders.filter((rider) =>
				`${rider.first_name} ${rider.last_name}`
					.toLowerCase()
					.includes(riderSeachField.toLowerCase()),
			),
		);
	}, [riderSeachField, filteredRiders]);

	const handleClearSearch = () => {
		setRiderSearchField("");
		setRiderSearchResults(filteredRiders);
	};

	useEffect(() => {
		filterRidersAndUpdateResults(null);
	}, [selectedSeries, selectedSport, genderFilter]);

	const fetchEvents = async () => {
		try {
			dispatch(getEventsThunk());
		} catch (error) {
			console.error("Error fetching events:", error);
		}
	};

	const fetchSeasons = async () => {
		try {
			dispatch(fetchSeasonsThunk());
		} catch (error) {
			console.error("Error fetching seasons:", error);
		}
	};

	const fetchSeries = async () => {
		try {
			dispatch(fetchSeriesThunk());
		} catch (error) {
			console.error("Error fetching series:", error);
		}
	};

	const fetchSports = async () => {
		try {
			dispatch(fetchSportsThunk());
		} catch (error) {
			console.error("Error fetching sports:", error);
		}
	};

	useEffect(() => {
		if (riders && riders.length > 0) {
			setFilteredRiders(riders);
			initializeResults(riders);
		}
	}, [riders]);

	useEffect(() => {
		if (riders && riders.length > 0) {
			initializeResults(riders);
			setLoadedResultsEventId("");
			setLoadedResultsCount(null);
			setPdfImportSummary(null);
			setShowPdfReviewModal(false);
			setPdfReviewRows([]);
		}
	}, [selectedEvent, selectedSeries, selectedSport, selectedSeason]);

	const fetchRiders = async () => {
		try {
			dispatch(fetchRidersThunk());
		} catch (error) {
			console.error("Error fetching riders:", error);
		}
	};

	const sortRiders = (riders: RiderWithSports[], sortType: any) => {
		try {
			if (sortType === "asc") {
				return riders.sort((a, b) => a.last_name.localeCompare(b.last_name));
			} else {
				return riders.sort((a, b) => {
					if (selectedPhase) {
						const riderA = resultsData.find((res) => res.rider === a._id);
						const riderB = resultsData.find((res) => res.rider === b._id);

						const pointsA =
							riderA && riderA[selectedPhase]?.points
								? riderA[selectedPhase]?.points
								: 0;
						const pointsB =
							riderB && riderB[selectedPhase]?.points
								? riderB[selectedPhase]?.points
								: 0;

						return (pointsB ?? 0) - (pointsA ?? 0);
					}
					return 0;
				});
			}
		} catch (error) {
			console.error("Error sorting riders:", error);
		}
	};

	const filterRidersAndUpdateResults = (e: any) => {
		if (!riders || riders.length === 0) return;
		if (!selectedSeries && !selectedSport && !genderFilter) {
			setFilteredRiders(riders);
			return;
		}

		const filtered = riders.filter((rider) => {
			return (
				(!selectedSeries || rider.series?.includes(selectedSeries._id)) &&
				(!selectedSport || rider.sports?.includes(selectedSport._id)) &&
				(!genderFilter || rider.gender === genderFilter)
			);
		});

		const sorted = sortRiders(filtered, e);
		if (!sorted || sorted.length === 0) {
			return;
		}
		setFilteredRiders(sorted);
	};

	const buildInitialResults = (filteredRiders: RiderWithSports[]) => {
		return filteredRiders.map((rider: RiderWithSports) => {
			const eventObj = events.find((event) => event._id === selectedEvent._id);
			let eventDate: Date = new Date();
			if (eventObj && eventObj.date) {
				eventDate =
					eventObj.date instanceof Date
						? eventObj.date
						: new Date(eventObj.date);
			}
			return {
				rider: rider._id ?? "",
				event: selectedEvent._id ?? "",
				event_date: eventDate,
				series: selectedSeries._id ?? "",
				sport: selectedSport._id ?? "",
				season: selectedSeason._id ?? "",
				qualifying_data: emptyPhaseResult(),
				semis_data: emptyPhaseResult(),
				finals_data: emptyPhaseResult(),
				bestTrickRiderMen: false,
				peoplesChoiceRiderMen: false,
				bestTrickRiderWomen: false,
				peoplesChoiceRiderWomen: false,
			};
		});
	};

	const initializeResults = (filteredRiders: RiderWithSports[]) => {
		const initialResults = buildInitialResults(filteredRiders);
		setResultsData(initialResults);
	};

	const normalizeExistingResult = (result: any): Result => ({
		_id: result._id,
		rider: getEntityId(result.rider),
		event: getEntityId(result.event),
		event_date: result.event_date ? new Date(result.event_date) : new Date(),
		series: getEntityId(result.series),
		sport: getEntityId(result.sport),
		season: getEntityId(result.season),
		qualifying_data: {
			...emptyPhaseResult(),
			...(result.qualifying_data || {}),
		},
		semis_data: {
			...emptyPhaseResult(),
			...(result.semis_data || {}),
		},
		finals_data: {
			...emptyPhaseResult(),
			...(result.finals_data || {}),
		},
		total_points: result.total_points || 0,
		bestTrickRiderMen: result.bestTrickRiderMen || false,
		peoplesChoiceRiderMen:
			result.peoplesChoiceRiderMen || result.PeoplesChoiceRiderMen || false,
		bestTrickRiderWomen: result.bestTrickRiderWomen || false,
		peoplesChoiceRiderWomen:
			result.peoplesChoiceRiderWomen || result.PeoplesChoiceRiderWomen || false,
	});

	const handleInputChange = (
		riderId: string,
		phase: ResultPhase,
		field: string,
		value: string | number | boolean,
	) => {
		setResultsData((currentResults) =>
			currentResults.map((result) =>
				result.rider === riderId
					? {
							...result,
							[phase]: {
								...result[phase],
								[field]: value,
							},
					  }
					: result,
			),
		);
	};

	const handleCheckBoxChange = (
		riderId: string,
		field: string,
		value: boolean,
	) => {
		console.log("Setting checkbox:", riderId, field, value);
		console.log(
			"old rider data:",
			resultsData.find((res) => res.rider === riderId),
		);
		setResultsData((currentResults) =>
			currentResults.map((result) =>
				result.rider === riderId
					? {
							...result,
							[field]: value,
					  }
					: result,
			),
		);
	};

	console.log("Results Data:", resultsData);

	const buildSavePayload = () =>
		filteredRiders
			.map((rider) => {
				const result = resultsData.find((res) => res.rider === rider._id);
				if (result) {
					const totalPoints =
						(result.qualifying_data?.points || 0) +
						(result.semis_data?.points || 0) +
						(result.finals_data?.points || 0);

					return {
						_id: result._id,
						event: selectedEvent._id,
						event_date:
							events.find((event) => event._id === selectedEvent._id)?.date ||
							"",
						series: selectedSeries._id,
						sport: selectedSport._id,
						season: selectedSeason._id,
						rider: rider._id,
						qualifying_data: result.qualifying_data || {},
						semis_data: result.semis_data || {},
						finals_data: result.finals_data || {},
						total_points: totalPoints,
						bestTrickRiderMen: result.bestTrickRiderMen || false,
						PeoplesChoiceRiderMen:
							result.peoplesChoiceRiderMen ||
							result.PeoplesChoiceRiderMen ||
							false,
						bestTrickRiderWomen: result.bestTrickRiderWomen || false,
						PeoplesChoiceRiderWomen:
							result.peoplesChoiceRiderWomen ||
							result.PeoplesChoiceRiderWomen ||
							false,
					};
				} else {
					return null;
				}
			})
			.filter(
				(entry) =>
					entry &&
					(entry._id ||
						(entry.qualifying_data?.points ?? 0) > 0 ||
						entry.qualifying_data?.time ||
						(entry.qualifying_data?.rank ?? 0) > 0 ||
						(entry.semis_data?.points ?? 0) > 0 ||
						entry.semis_data?.time ||
						(entry.semis_data?.rank ?? 0) > 0 ||
						(entry.finals_data?.points ?? 0) > 0 ||
						entry.finals_data?.time ||
						(entry.finals_data?.rank ?? 0) > 0),
			);

	const handleLoadExistingResults = async () => {
		if (!selectedEvent._id || !selectedSeries._id || !selectedSport._id) {
			alert("Please select an event, series, and sport");
			return;
		}

		try {
			setIsLoadingResults(true);
			const existingResults = await getResults(selectedEvent._id);
			const normalizedResults = Array.isArray(existingResults)
				? existingResults.map(normalizeExistingResult)
				: [];
			const resultsByRider = new Map(
				normalizedResults.map((result) => [result.rider, result]),
			);

			const mergedResults = buildInitialResults(riders).map((initialResult) => {
				const existingResult = resultsByRider.get(initialResult.rider);

				return existingResult
					? {
							...initialResult,
							...existingResult,
							event: selectedEvent._id ?? "",
							series: selectedSeries._id ?? "",
							sport: selectedSport._id ?? "",
							season: selectedSeason._id ?? "",
					  }
					: initialResult;
			});

			setResultsData(mergedResults);
			setLoadedResultsEventId(selectedEvent._id);
			setLoadedResultsCount(normalizedResults.length);
		} catch (error) {
			console.error("Error loading results:", error);
			alert("Error loading results:" + error);
		} finally {
			setIsLoadingResults(false);
		}
	};

	const getEligibleImportRiders = () =>
		riders.filter((rider) => {
			return (
				(!selectedSeries._id || rider.series?.includes(selectedSeries._id)) &&
				(!selectedSport._id || rider.sports?.includes(selectedSport._id)) &&
				(!genderFilter || rider.gender === genderFilter)
			);
		});

	const getBestGuessRider = (
		pdfRow: ResultsPdfImportRow,
		eligibleRiders: RiderWithSports[],
	) => {
		return eligibleRiders.reduce<{
			rider?: RiderWithSports;
			score: number;
		}>(
			(bestMatch, rider) => {
				const score = Math.max(
					getNameSimilarity(
						pdfRow.name,
						`${rider.last_name} ${rider.first_name}`,
					),
					getNameSimilarity(
						pdfRow.name,
						`${rider.first_name} ${rider.last_name}`,
					),
				);

				return score > bestMatch.score ? { rider, score } : bestMatch;
			},
			{ score: 0 },
		);
	};

	const buildPdfReviewRows = (
		importSummary: ResultsPdfImportSummary,
	): ResultsPdfReviewRow[] => {
		const eligibleRiders = getEligibleImportRiders();
		const allRows = [...importSummary.matches, ...importSummary.unmatchedRows];

		return allRows
				.map((row, index) => {
					const bestGuess = row.rider
						? { rider: undefined, score: undefined }
						: getBestGuessRider(row, eligibleRiders);
					const bestGuessRiderId = bestGuess.rider?._id;
					const shouldAssignBestGuess =
						(bestGuess.score ?? 0) > BEST_GUESS_ASSIGNMENT_THRESHOLD;

					return {
						...row,
						key: `${row.uciId}-${row.rank ?? "unranked"}-${index}`,
						selectedRiderId:
							row.rider?._id ||
							(shouldAssignBestGuess ? bestGuessRiderId || "" : ""),
						riderSearch: "",
						isExactMatch: Boolean(row.rider?._id),
						ignored: false,
						bestGuessRiderId,
					bestGuessScore: bestGuess.score,
				};
			})
			.sort((a, b) => {
				if (a.rank === null && b.rank === null) {
					return 0;
				}
				if (a.rank === null) {
					return 1;
				}
				if (b.rank === null) {
					return -1;
				}
				return a.rank - b.rank;
			});
	};

	const applyPdfRowsToResults = (rowsToApply: ResultsPdfReviewRow[]) => {
		const rowsByRiderId = new Map<string, ResultsPdfReviewRow>();

		rowsToApply.forEach((row) => {
			if (row.selectedRiderId) {
				rowsByRiderId.set(row.selectedRiderId, row);
			}
		});

		setResultsData((currentResults) => {
			const baseResults =
				currentResults.length > 0 ? currentResults : buildInitialResults(riders);

			return baseResults.map((result) => {
				const importedRow = rowsByRiderId.get(result.rider);
				if (!importedRow) {
					return result;
				}

				return {
					...result,
					[selectedPhase]: {
						...emptyPhaseResult(),
						...result[selectedPhase],
						time: importedRow.time,
						points: importedRow.points,
						rank: importedRow.rank ?? 0,
					},
				};
			});
		});
	};

	const handlePdfReviewRowChange = (
		rowKey: string,
		field: "selectedRiderId" | "points" | "ignored" | "riderSearch",
		value: string | number | boolean,
	) => {
		setPdfReviewRows((currentRows) =>
			currentRows.map((row) => {
				if (row.key !== rowKey) {
					return row;
				}

				if (field === "selectedRiderId") {
					return {
						...row,
						selectedRiderId: String(value),
						ignored: false,
					};
				}

				if (field === "ignored") {
					return {
						...row,
						selectedRiderId: "",
						ignored: Boolean(value),
					};
				}

				if (field === "riderSearch") {
					return {
						...row,
						riderSearch: String(value),
					};
				}

				return { ...row, points: Number(value) };
			}),
		);
	};

	const handleRestorePdfReviewRow = (rowKey: string) => {
		setPdfReviewRows((currentRows) =>
			currentRows.map((row) =>
					row.key === rowKey
						? {
								...row,
								selectedRiderId:
									row.rider?._id ||
									((row.bestGuessScore ?? 0) >
									BEST_GUESS_ASSIGNMENT_THRESHOLD
										? row.bestGuessRiderId || ""
										: ""),
								ignored: false,
						  }
					: row,
			),
		);
	};

	const getPdfReviewRowReason = (
		row: ResultsPdfReviewRow,
		hasDuplicateAssignment: boolean,
		duplicatePdfRiderNames: string[],
	) => {
		if (hasDuplicateAssignment) {
			return `Duplicate rider assignment: ${duplicatePdfRiderNames.join(" / ")}`;
		}

		if (row.ignored) {
			return "Ignored by admin";
		}

		if (!row.selectedRiderId) {
			if (row.bestGuessScore !== undefined) {
				return row.bestGuessScore > BEST_GUESS_ASSIGNMENT_THRESHOLD
					? `Best guess ${Math.round(row.bestGuessScore * 100)}% was cleared`
					: `Best guess ${Math.round(
							row.bestGuessScore * 100,
					  )}% below 75% threshold`;
			}

			return "No matching rider found";
		}

		if (!row.isExactMatch) {
			return row.bestGuessScore !== undefined &&
				row.selectedRiderId === row.bestGuessRiderId
				? `Best guess ${Math.round(row.bestGuessScore * 100)}% from PDF name`
				: "Manually assigned";
		}

		return "Exact match ready";
	};

	const handleApplyPdfReview = () => {
		const assignedRows = pdfReviewRows.filter((row) => row.selectedRiderId);
		const uniqueAssignedRiderIds = new Set(
			assignedRows.map((row) => row.selectedRiderId),
		);

		if (assignedRows.length === 0) {
			alert("Assign at least one rider before applying the import");
			return;
		}

		if (uniqueAssignedRiderIds.size !== assignedRows.length) {
			alert("A rider is assigned to more than one PDF row.");
			return;
		}

		applyPdfRowsToResults(assignedRows);
		setShowPdfReviewModal(false);
		setPdfImportSummary((currentSummary) =>
			currentSummary
				? {
						...currentSummary,
						matchedCount: assignedRows.length,
						unmatchedCount: pdfReviewRows.length - assignedRows.length,
						matches: assignedRows,
						unmatchedRows: pdfReviewRows.filter((row) => !row.selectedRiderId),
				  }
				: currentSummary,
		);
	};

	const handleImportResultsPdf = async () => {
		if (
			!selectedResultsPdf ||
			!selectedEvent._id ||
			!selectedSeries._id ||
			!selectedSport._id ||
			!selectedSeason._id
		) {
			alert("Please select an event, series, sport, season, and PDF file");
			return;
		}

		try {
			setIsImportingPdf(true);
			const formData = new FormData();
			formData.append("pdf", selectedResultsPdf);
			formData.append("sport", selectedSport._id);
			formData.append("series", selectedSeries._id);
			formData.append("season", selectedSeason._id);
			formData.append("event", selectedEvent._id);
			formData.append("phase", selectedPhase);
			formData.append("gender", genderFilter);

			const importSummary: ResultsPdfImportSummary =
				await importResultsPdfPreview(formData);

			setPdfImportSummary(importSummary);
			setPdfReviewRows(buildPdfReviewRows(importSummary));
			setShowPdfReviewModal(true);
		} catch (error) {
			console.error("Error importing results PDF:", error);
			alert("Error importing results PDF: " + getImportErrorMessage(error));
		} finally {
			setIsImportingPdf(false);
		}
	};

	const handleSaveResults = async () => {
		try {
			if (!selectedEvent._id || !selectedSeries._id || !selectedSport._id) {
				alert("Please select an event, series, and sport");
				return;
			}

			const data = buildSavePayload();
			if (data.length === 0) {
				alert("No results to save");
				return;
			}

			const isUpdatingLoadedResults = loadedResultsEventId === selectedEvent._id;
			const res = await upsertResults(data);
			if (res) {
				alert(
					isUpdatingLoadedResults
						? "Results updated successfully"
						: "Results saved successfully",
				);
				fetchRiders();
				setSelectedEvent(initialEvent);
				setSelectedSeries(initialSeries);
				setSelectedSport(initialSport);
				setSelectedSeason(initialSeason);
				setLoadedResultsEventId("");
				setLoadedResultsCount(null);
				setResultsData([]);
			}
		} catch (error) {
			console.error("Error saving results:", error);
		}
	};

	const handleAddRank = () => {
		const sortedRiders = sortRiders(filteredRiders, "points") || [];
		setResultsData((currentResults) =>
			currentResults.map((result) => {
				const sortedRider = sortedRiders.find(
					(rider) => rider._id === result.rider,
				);

				if (
					sortedRider &&
					result[selectedPhase] &&
					(result[selectedPhase]?.points ?? 0) > 0
				) {
					return {
						...result,
						[selectedPhase]: {
							...result[selectedPhase],
							rank: sortedRiders.indexOf(sortedRider) + 1,
						},
					};
				}
				return result;
			}),
		);
	};

	const handleUpdateLeaderboards = async () => {
		try {
			const res = await updateLeaderboard();
			console.log("Leaderboards updated:", res.data);
		} catch (error) {
			alert("Error updating leaderboards:" + error);
		}
	};

	const handleAddBrandTeamToTeamResults = async () => {
		try {
			const res = await addBrandTeamToTeamResults();
			console.log("Brand team added to team results:", res.data);
		} catch (error) {
			console.error("Error adding brand team to team results:", error);
		}
	};

	const eligibleImportRiders = [...getEligibleImportRiders()].sort((a, b) =>
		getRiderDisplayName(a).localeCompare(getRiderDisplayName(b)),
	);
	const assignedPdfReviewRows = pdfReviewRows.filter((row) => row.selectedRiderId);
	const unassignedPdfReviewRowsCount =
		pdfReviewRows.length - assignedPdfReviewRows.length;
	const ignoredPdfReviewRowsCount = pdfReviewRows.filter(
		(row) => row.ignored,
	).length;
	const selectedPdfReviewRiderIds = assignedPdfReviewRows.map(
		(row) => row.selectedRiderId,
	);
	const duplicatePdfReviewRiderIds = new Set(
		selectedPdfReviewRiderIds.filter(
			(riderId, index) => selectedPdfReviewRiderIds.indexOf(riderId) !== index,
		),
	);

	return (
		<AdminLayout>
			<Container className=" min-vh-100 py-4">
				<h1 className="text-light mb-4">Admin Results Page</h1>
				<Row className="mb-4">
					<Col>
						<AdminResultsInstructionsAccordion
							onUpdateBrandTeams={handleAddBrandTeamToTeamResults}
							onUpdateLeaderboards={handleUpdateLeaderboards}
						/>
					</Col>
				</Row>
				<Row>
					<Col>
						<Card className="mb-3 bg-secondary text-light">
							<Card.Body>
								<div className="d-flex flex-row justify-content-end gap-2">
									<Button
										variant="outline-light"
										onClick={handleLoadExistingResults}
										disabled={
											isLoadingResults ||
											!selectedEvent._id ||
											!selectedSeries._id ||
											!selectedSport._id
										}
									>
										{isLoadingResults ? "Loading..." : "Load Existing Results"}
									</Button>
									<Button variant="primary" onClick={handleSaveResults}>
										{loadedResultsEventId === selectedEvent._id
											? "Update Results"
											: "Save Results"}
									</Button>
								</div>
								<Row className="mb-3">
									<Col>
										<Form>
											<Form.Group>
												<Form.Label className="text-light">
													Select Series
												</Form.Label>
												<Form.Control
													as="select"
													className="bg-dark text-light border-secondary"
													onChange={(e) => {
														const selected = series.find(
															(series) => series._id === e.target.value,
														);
														setSelectedSeries(selected || initialSeries);
													}}
													value={selectedSeries._id}
												>
													<option value="">Select Series</option>
													{series.map((series) => (
														<option key={series._id} value={series._id}>
															{series.name}
														</option>
													))}
												</Form.Control>
											</Form.Group>
									</Form>
								</Col>
								<Col>
									<Form>
										<Form.Group>
											<Form.Label className="text-light">
												Select Sport
											</Form.Label>
											<Form.Control
												as="select"
												className="bg-dark text-light border-secondary"
												onChange={(e) => {
													const selected = sports.find(
														(sport) => sport._id === e.target.value,
													);
													setSelectedSport(selected || initialSport);
												}}
												value={selectedSport._id}
											>
												<option value="">Select Sport</option>
												{sports.map((sport) => (
													<option key={sport._id} value={sport._id}>
														{sport.name}
													</option>
												))}
											</Form.Control>
										</Form.Group>
									</Form>
								</Col>
								<Col>
									<Form>
										<Form.Group>
											<Form.Label className="text-light">
												Select Season
											</Form.Label>
											<Form.Control
												as="select"
												className="bg-dark text-light border-secondary"
												onChange={(e) => {
													const selected = seasons.find(
														(season) => season._id === e.target.value,
													);
													setSelectedSeason(selected || initialSeason);
												}}
												value={selectedSeason._id}
											>
												<option value="">Select Season</option>
												{seasons.map((season: Season) => (
													<option key={season._id} value={season._id}>
														{season.name}
													</option>
												))}
											</Form.Control>
										</Form.Group>
									</Form>
								</Col>
							</Row>
							<Form>
								<Form.Group className="mb-3">
									<Form.Label className="text-light">Select Event</Form.Label>
									<Form.Control
										as="select"
										className="bg-dark text-light border-secondary"
										onChange={(e) => {
											const selected = events.find(
												(event) => event._id === e.target.value,
											);
											setSelectedEvent(selected || initialEvent);
										}}
										value={selectedEvent._id}
									>
										<option value="">Select Event</option>
										{filteredEvents.map((event) => (
											<option key={event._id} value={event._id}>
												{event.name}
											</option>
										))}
									</Form.Control>
									{loadedResultsEventId === selectedEvent._id && (
										<Form.Text className="text-light">
											Loaded {loadedResultsCount ?? 0} existing result
											{loadedResultsCount === 1 ? "" : "s"} for this event.
										</Form.Text>
									)}
								</Form.Group>
								<Row className="mb-3">
									<Col>
										<Form.Group>
											<Form.Label className="text-light">Sort</Form.Label>
											<Form.Select
												aria-label="Sort select"
												value={sort}
												className="mb-3 bg-dark text-light border-secondary"
												onChange={(e) => {
													setSort(e.target.value);
													filterRidersAndUpdateResults(e.target.value);
												}}
											>
												<option value="asc">A-Z</option>
												<option value="desc">Points</option>
											</Form.Select>
										</Form.Group>
									</Col>
									<Col>
										<Form.Group>
											<Form.Label className="text-light">Gender</Form.Label>
											<Form.Select
												aria-label="Gender Select"
												value={genderFilter}
												className="mb-3 bg-dark text-light border-secondary"
												onChange={(e) => {
													setGenderFilter(e.target.value);
												}}
											>
												<option value="m">Men</option>
												<option value="f">Women</option>
											</Form.Select>
										</Form.Group>
									</Col>
									<Col>
										<Form.Group>
											<Form.Label className="text-light">Phase</Form.Label>
											<Form.Select
												aria-label="Phase select"
												value={selectedPhase}
												className="mb-3 bg-dark text-light border-secondary"
												onChange={(e) =>
													setSelectedPhase(e.target.value as ResultPhase)
												}
											>
												<option value={ResultPhase.Qualifying}>
													Qualifying
												</option>
												<option value={ResultPhase.Semis}>Semis</option>
												<option value={ResultPhase.Finals}>Finals</option>
											</Form.Select>
										</Form.Group>
									</Col>
								</Row>
								<Row className="align-items-end mb-3">
									<Col md={8}>
										<Form.Group>
											<Form.Label className="text-light">
												Import Results PDF
											</Form.Label>
											<Form.Control
												type="file"
												accept="application/pdf"
												className="bg-dark text-light border-secondary"
												onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
													setSelectedResultsPdf(e.target.files?.[0] || null);
													setPdfImportSummary(null);
												}}
											/>
										</Form.Group>
									</Col>
									<Col md={4}>
										<Button
											variant="outline-light"
											className="w-100"
											onClick={handleImportResultsPdf}
											disabled={
												isImportingPdf ||
												!selectedResultsPdf ||
												!selectedEvent._id ||
												!selectedSeries._id ||
												!selectedSport._id ||
												!selectedSeason._id
											}
										>
											{isImportingPdf
												? "Importing..."
												: `Import PDF to ${selectedPhaseLabel}`}
										</Button>
									</Col>
								</Row>
								{pdfImportSummary && (
									<Alert
										variant={
											pdfImportSummary.unmatchedCount > 0 ? "warning" : "success"
										}
									>
										<div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
											<div>
												Parsed {pdfImportSummary.parsedCount} PDF rows.{" "}
												{pdfImportSummary.matchedCount} assigned to{" "}
												{selectedPhaseLabel}.
												{pdfImportSummary.unmatchedCount > 0 &&
													` ${pdfImportSummary.unmatchedCount} rows still need rider assignment.`}
											</div>
											{pdfReviewRows.length > 0 && (
												<Button
													variant="outline-dark"
													size="sm"
													onClick={() => setShowPdfReviewModal(true)}
												>
													Review Import
												</Button>
											)}
										</div>
										{pdfImportSummary.unmatchedRows.length > 0 && (
											<div className="mt-2 small">
												Unmatched:{" "}
												{pdfImportSummary.unmatchedRows
													.slice(0, 8)
													.map(
														(row) =>
															`${row.rank ? `${row.rank}. ` : ""}${row.name}`,
													)
													.join(", ")}
												{pdfImportSummary.unmatchedRows.length > 8 ? ", ..." : ""}
											</div>
										)}
									</Alert>
								)}
								<div className="text-end w-100">
									<Button
										className="mb-3 text-white"
										variant="success"
										onClick={() => {
											handleAddRank();
										}}
									>
										Add Rank to {selectedPhase}
									</Button>
								</div>
								<Form.Group className="mb-3">
									<Form.Label className="text-light">Search Rider</Form.Label>
									<div className="d-flex">
										<Form.Control
											type="text"
											placeholder="Search by name"
											value={riderSeachField}
											className="bg-dark text-light border-secondary custom-input custom-placeholder"
											onChange={(e) => setRiderSearchField(e.target.value)}
										/>
										<Button
											variant="secondary"
											className="ms-2"
											onClick={handleClearSearch}
											disabled={!riderSeachField}
										>
											Clear
										</Button>
									</div>
								</Form.Group>
								{selectedEvent._id &&
									selectedSport._id !== "" &&
									selectedSeries._id !== "" &&
									selectedSeason._id !== "" && (
										<Table
											striped
											bordered
											hover
											variant="dark"
											className="table-dark"
										>
											<thead>
												<tr>
													<th>Name</th>
													{selectedSport.name.toLowerCase() ===
														FantasySports.SlopeStyle.toLowerCase() && (
														<>
															<th>Score</th>
															<th>Best Trick (Men)</th>
															<th>Best Trick (Women)</th>
															<th>Peoples Choice (Men)</th>
															<th>Peoples Choice (Women)</th>
														</>
													)}

													<th style={{ width: "7rem" }}>Points</th>
													<th style={{ width: "7rem" }}>Rank</th>
												</tr>
											</thead>
											<tbody>
												{Array.isArray(riderSearchResults) &&
													riderSearchResults.length > 0 &&
													resultsData &&
													resultsData.length > 0 &&
													riderSearchResults.map((rider: RiderWithSports) => (
														<tr key={rider._id}>
															<td>
																{rider.last_name}, {rider.first_name}
															</td>
															{selectedSport.name.toLowerCase() ===
																FantasySports.SlopeStyle.toLowerCase() && (
																<>
																	<td style={{ width: "7rem" }}>
																		<Form.Control
																			type="number"
																			className="mt-0 py-0 bg-dark text-light border-secondary"
																			value={
																				resultsData.find(
																					(res) => res.rider === rider._id,
																				)?.[selectedPhase]?.score || 0
																			}
																			onChange={(e) =>
																				handleInputChange(
																					rider._id ?? "",
																					selectedPhase,
																					"score",
																					Number(e.target.value),
																				)
																			}
																		/>
																	</td>
																	<td>
																		<Form.Check
																			type="checkbox"
																			className="my-2"
																			checked={
																				resultsData.find(
																					(res) => res.rider === rider._id,
																				)?.bestTrickRiderMen || false
																			}
																			onChange={(e) =>
																				handleCheckBoxChange(
																					rider._id ?? "",
																					"bestTrickRiderMen",
																					e.target.checked,
																				)
																			}
																		/>
																	</td>
																	<td>
																		<Form.Check
																			type="checkbox"
																			className="my-2"
																			checked={
																				resultsData.find(
																					(res) => res.rider === rider._id,
																				)?.bestTrickRiderWomen || false
																			}
																			onChange={(e) =>
																				handleCheckBoxChange(
																					rider._id ?? "",
																					"bestTrickRiderWomen",
																					e.target.checked,
																				)
																			}
																		/>
																	</td>
																	<td>
																		<Form.Check
																			type="checkbox"
																			className="my-2"
																			checked={
																				resultsData.find(
																					(res) => res.rider === rider._id,
																				)?.peoplesChoiceRiderMen || false
																			}
																			onChange={(e) =>
																				handleCheckBoxChange(
																					rider._id ?? "",
																					"peoplesChoiceRiderMen",
																					e.target.checked,
																				)
																			}
																		/>
																	</td>
																	<td>
																		<Form.Check
																			type="checkbox"
																			className="my-2"
																			checked={
																				resultsData.find(
																					(res) => res.rider === rider._id,
																				)?.peoplesChoiceRiderWomen || false
																			}
																			onChange={(e) =>
																				handleCheckBoxChange(
																					rider._id ?? "",
																					"peoplesChoiceRiderWomen",
																					e.target.checked,
																				)
																			}
																		/>
																	</td>
																</>
															)}
															<td>
																<Form.Control
																	type="number"
																	className="my-0 py-0 bg-dark text-light border-secondary"
																	value={
																		resultsData.find(
																			(res) => res.rider === rider._id,
																		)?.[selectedPhase]?.points || 0
																	}
																	onChange={(e) =>
																		handleInputChange(
																			rider._id ?? "",
																			selectedPhase,
																			"points",
																			Number(e.target.value),
																		)
																	}
																/>
															</td>
															<td>
																<Form.Control
																	type="number"
																	className="mt-0 py-0 bg-dark text-light border-secondary"
																	value={
																		resultsData.find(
																			(res) => res.rider === rider._id,
																		)?.[selectedPhase]?.rank || 0
																	}
																	onChange={(e) =>
																		handleInputChange(
																			rider._id ?? "",
																			selectedPhase,
																			"rank",
																			Number(e.target.value),
																		)
																	}
																/>
															</td>
														</tr>
													))}
											</tbody>
										</Table>
									)}
							</Form>
						</Card.Body>
					</Card>
				</Col>
			</Row>
			</Container>
				<Modal
					show={showPdfReviewModal}
					onHide={() => setShowPdfReviewModal(false)}
					size="xl"
					dialogClassName="results-import-review-modal"
					centered
				>
				<Modal.Header closeButton>
					<Modal.Title>Review Results PDF Import</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<div className="d-flex flex-wrap gap-3 mb-3">
						<span>{pdfReviewRows.length} PDF rows</span>
						<span>{assignedPdfReviewRows.length} assigned</span>
						<span>{unassignedPdfReviewRowsCount} unassigned</span>
						<span>{ignoredPdfReviewRowsCount} ignored</span>
						<span>{selectedPhaseLabel}</span>
					</div>
					<Alert variant="secondary">
						<div>
							<strong>Legend:</strong> Red borders mean the same rider is
							assigned to multiple PDF rows. Ignored and unassigned rows are not
							applied to the results table.
						</div>
						<div className="mt-1">
							{unassignedPdfReviewRowsCount} rows will remain unassigned after
							apply, including {ignoredPdfReviewRowsCount} ignored rows.
						</div>
					</Alert>
					{duplicatePdfReviewRiderIds.size > 0 && (
						<Alert variant="danger">
							A rider is assigned to more than one PDF row. Resolve duplicates
							before applying.
						</Alert>
					)}
					<div style={{ maxHeight: "65vh", overflowY: "auto" }}>
						<Table striped bordered hover variant="dark" responsive>
							<thead>
								<tr>
									<th style={{ width: "5rem" }}>Rank</th>
									<th>PDF Rider</th>
										<th style={{ width: "8rem" }}>Points</th>
										<th>Assigned Rider</th>
											<th style={{ width: "22rem" }}>Reason</th>
											<th style={{ width: "9rem" }}>Action</th>
										</tr>
									</thead>
							<tbody>
									{pdfReviewRows.map((row) => {
											const hasDuplicateAssignment = Boolean(
												row.selectedRiderId &&
													duplicatePdfReviewRiderIds.has(row.selectedRiderId),
											);
											const normalizedRiderSearch = normalizeImportName(
												row.riderSearch,
											);
											const filteredEligibleImportRiders = normalizedRiderSearch
												? eligibleImportRiders.filter((rider) => {
														const lastFirstName = normalizeImportName(
															getRiderDisplayName(rider),
														);
														const firstLastName = normalizeImportName(
															`${rider.first_name} ${rider.last_name}`,
														);

														return (
															lastFirstName.includes(normalizedRiderSearch) ||
															firstLastName.includes(normalizedRiderSearch)
														);
												  })
												: eligibleImportRiders;
											const selectedRiderOption = eligibleImportRiders.find(
												(rider) => rider._id === row.selectedRiderId,
											);
											const riderOptions =
												selectedRiderOption &&
												!filteredEligibleImportRiders.some(
													(rider) => rider._id === selectedRiderOption._id,
												)
													? [
															selectedRiderOption,
															...filteredEligibleImportRiders,
													  ]
													: filteredEligibleImportRiders;
											const duplicatePdfRiderNames = row.selectedRiderId
												? pdfReviewRows
														.filter(
														(pdfRow) =>
															pdfRow.selectedRiderId === row.selectedRiderId,
													)
													.map(
														(pdfRow) =>
															`${pdfRow.rank ? `${pdfRow.rank}. ` : ""}${pdfRow.name}`,
													)
											: [];

									return (
										<tr key={row.key}>
											<td>{row.rank ?? "-"}</td>
											<td>
												<div>{row.name}</div>
												<div className="small text-muted">
													Bib {row.bib || "-"} · UCI {row.uciId} ·{" "}
													{row.time || "-"}
												</div>
											</td>
											<td>
												<Form.Control
													type="number"
													className="bg-dark text-light border-secondary"
													value={row.points}
													onChange={(e) =>
														handlePdfReviewRowChange(
															row.key,
															"points",
															Number(e.target.value),
														)
													}
												/>
											</td>
												<td>
													<Form.Control
														type="text"
														placeholder="Filter riders"
														className="mb-2 bg-dark text-light border-secondary"
														value={row.riderSearch}
														onChange={(e) =>
															handlePdfReviewRowChange(
																row.key,
																"riderSearch",
																e.target.value,
															)
														}
													/>
													<Form.Select
														className={`bg-dark text-light ${
															hasDuplicateAssignment
															? "border-danger"
															: "border-secondary"
													}`}
													value={row.selectedRiderId}
													onChange={(e) =>
														handlePdfReviewRowChange(
															row.key,
															"selectedRiderId",
															e.target.value,
														)
													}
													>
														<option value="">No rider assigned</option>
														{riderOptions.map((rider) => (
															<option key={rider._id} value={rider._id}>
																{getRiderDisplayName(rider)}
															</option>
														))}
													</Form.Select>
													{normalizedRiderSearch &&
														filteredEligibleImportRiders.length === 0 && (
															<Form.Text className="text-warning">
																No riders match this filter.
															</Form.Text>
														)}
												</td>
											<td
												className={
													hasDuplicateAssignment || row.ignored || !row.selectedRiderId
														? "text-warning"
														: ""
												}
											>
												{getPdfReviewRowReason(
													row,
													hasDuplicateAssignment,
													duplicatePdfRiderNames,
												)}
											</td>
											<td>
												{row.ignored ? (
													<Button
														variant="outline-light"
														size="sm"
														onClick={() => handleRestorePdfReviewRow(row.key)}
													>
														Restore
													</Button>
												) : (
													<Button
														variant="outline-warning"
														size="sm"
														onClick={() =>
															handlePdfReviewRowChange(
																row.key,
																"ignored",
																true,
															)
														}
													>
														Ignore
													</Button>
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</Table>
					</div>
				</Modal.Body>
				<Modal.Footer>
					<Button
						variant="outline-light"
						onClick={() => setShowPdfReviewModal(false)}
					>
						Close
					</Button>
					<Button
						variant="primary"
						onClick={handleApplyPdfReview}
						disabled={
							assignedPdfReviewRows.length === 0 ||
							duplicatePdfReviewRiderIds.size > 0
						}
					>
						Apply to {selectedPhaseLabel}
					</Button>
				</Modal.Footer>
			</Modal>
		</AdminLayout>
	);
};

export default AdminResultsPage;
